import * as React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  SCHEDULE_MINT_DARK,
  calendarDayChipSx,
  primeTimeBadgeSx,
  scheduleHeaderGradient,
  slotChipSx,
} from "./scheduleUi";
import {
  BuildTempSlot,
  CalendarResponse,
  ResponseObject,
  ServiceHub,
  SlotRequest,
} from "@/app/classes/main";
import { Loader } from "../MainLoader/Loader";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "@/app/services/HolderService";
import { useCurrencyCode } from "../helper-functions/CurrencySymbol";
import "./index.css";

interface ScheduleCartMobileProps {
  setSelectedDate: any;
  setSelectedTime: any;
  calender: any;
  cid: any;
  setUpdateSlotsCartMobile: any;
  setSkeltonLoaderPayment: any;
  setSkeltonLoader: any;
  skeltonLoader: any;
}

const ScheduleCartMobile: React.FC<ScheduleCartMobileProps> = ({
  setSelectedDate,
  setSelectedTime,
  calender,
  cid,
  setUpdateSlotsCartMobile,
  setSkeltonLoaderPayment,
  setSkeltonLoader,
  skeltonLoader,
}) => {
  const [loading, setLoading] = React.useState(false);
  const apiService: ApiService = React.useMemo(() => new ApiService(), []);
  const [slots, setSlots] = React.useState([]);
  const [date, setDate] = React.useState(new CalendarResponse());
  const [slot, setSlot] = React.useState(new BuildTempSlot());
  const currencyCode = useCurrencyCode();

  const handleSelectedTime = React.useCallback(
    (selectedTime: any) => {
      localStorage.setItem("slot", btoa(JSON.stringify(selectedTime)));
      setSlot(selectedTime);
      setSelectedTime(selectedTime?.slot);
      setUpdateSlotsCartMobile();
      setSkeltonLoader(false);
      setSkeltonLoaderPayment(true);
    },
    [
      setSelectedTime,
      setUpdateSlotsCartMobile,
      setSkeltonLoader,
      setSkeltonLoaderPayment,
    ]
  );

  const handleSelectedDate = React.useCallback(
    (date: any) => {
      // setLoading(true);
      setSkeltonLoader(true);
      setSkeltonLoaderPayment(true);
      setDate(date);
      localStorage.setItem("calendar", btoa(JSON.stringify(date)));
      setSelectedDate(date.date);
      const slotRequest: SlotRequest = new SlotRequest();
      slotRequest.date = date.date;
      const serviceHub: ServiceHub = JSON.parse(
        atob(localStorage.getItem("serviceHub") as string)
      );
      slotRequest.hid = serviceHub.id;
      slotRequest.admin = false;
      slotRequest.cid = cid;
      if (cid !== null) {
        apiService
          .post(apiService.slots, slotRequest, getToken()!)
          .then((value: unknown) => {
            const responseObject = value as ResponseObject;
            if (responseObject?.status == "success") {
              setSlots(responseObject?.object);
              handleSelectedTime(responseObject?.object[0]);
              setLoading(false);
            } else {
              setLoading(false);
            }
          });
      }
    },
    [apiService, setSelectedDate, handleSelectedTime, cid]
  );

  React.useEffect(() => {
    const calendarRow = localStorage.getItem("calendar");
    if (calendarRow != null) {
      const cal: CalendarResponse = JSON.parse(
        atob(localStorage.getItem("calendar") as string) as string
      );
      handleSelectedDate(cal);
    }
    const slotRow = localStorage.getItem("slot");
    if (slotRow != null) {
      try {
        const decodedData = atob(slotRow);
        if (decodedData && decodedData !== "undefined") {
          const buildTempSlot: BuildTempSlot = JSON.parse(decodedData);
          handleSelectedTime(buildTempSlot);
        } else {
          console.log("Decoded data is empty or 'undefined'.");
        }
      } catch (error) {
        console.error("Error decoding or parsing slot data:", error);
      }
    } else {
      console.log("No data found in localStorage for key 'slot'.");
    }
  }, [cid]);

  return (
    <React.Fragment>
      {loading && <Loader />}
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          borderRadius: "20px",
          border: "1px solid rgba(31, 122, 77, 0.12)",
          boxShadow: "0 8px 32px rgba(15, 23, 17, 0.08)",
          overflow: "hidden",
          bgcolor: "#fafcfb",
        }}
      >
        <Box
          sx={{
            background: scheduleHeaderGradient,
            px: 2,
            py: 1.5,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, color: "#fff", letterSpacing: "-0.02em" }}
          >
            Date & time
          </Typography>
          <Typography variant="caption" sx={{ color: alpha("#fff", 0.88) }}>
            Pick a day, then choose a slot
          </Typography>
        </Box>
        <Box
          sx={{
            bgcolor: "#fff",
            px: 1.5,
            pt: 2,
            pb: 1.5,
            width: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "flex-start",
              alignItems: "stretch",
              gap: 0,
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              pb: 0.5,
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorX: "contain",
              touchAction: "pan-x",
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                background: alpha("#1f7a4d", 0.28),
              },
            }}
          >
            {calender?.map((pair: any, index: any) => (
              <Box
                key={pair?.date}
                sx={calendarDayChipSx(date.date === pair.date)}
                onClick={() => handleSelectedDate(pair)}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    opacity: 0.92,
                    fontWeight: 600,
                    fontSize: "0.7rem",
                  }}
                >
                  {pair?.dayShort}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 800, lineHeight: 1.2, my: 0.25 }}
                >
                  {pair?.day}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.85, fontSize: "0.65rem" }}>
                  {pair?.monthString?.slice(0, 3)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          sx={{
            bgcolor: "#fff",
            px: 1.5,
            pb: 1,
            pt: 0,
            borderTop: `1px solid ${alpha("#1f7a4d", 0.1)}`,
            width: "100%",
            minWidth: 0,
            maxWidth: "100%",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 800,
              color: SCHEDULE_MINT_DARK,
              letterSpacing: "-0.02em",
              py: 1,
            }}
          >
            Available slots
          </Typography>
          <Box
            sx={{
              width: "100%",
              minWidth: 0,
              minHeight: 0,
              maxWidth: "100%",
              overflowX: "auto",
              overflowY: "hidden",
              pb: 0.5,
              WebkitOverflowScrolling: "touch",
              overscrollBehaviorX: "contain",
              touchAction: "pan-x",
              "&::-webkit-scrollbar": { height: 8 },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: 999,
                background: alpha("#1f7a4d", 0.28),
              },
            }}
          >
            {skeltonLoader ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  py: 1,
                  gap: 1,
                  width: "max-content",
                  minWidth: "min-content",
                }}
              >
                {Array.from({ length: 5 }).map((_, colIndex) => (
                  <Box
                    key={colIndex}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {Array.from({ length: 3 }).map((_, rowIndex) => (
                      <Skeleton
                        key={rowIndex}
                        variant="rounded"
                        width={150}
                        height={52}
                        animation="wave"
                        sx={{ borderRadius: "12px" }}
                      />
                    ))}
                  </Box>
                ))}
              </Box>
            ) : slots.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "nowrap",
                  py: 0.5,
                  gap: 1,
                  width: "max-content",
                  minWidth: "min-content",
                  maxWidth: "none",
                }}
              >
                {Array.from({ length: Math.ceil(slots.length / 3) }).map(
                  (_, colIndex) => (
                    <Box
                      key={colIndex}
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        minWidth: "152px",
                      }}
                    >
                      {slots
                        .slice(colIndex * 3, colIndex * 3 + 3)
                        .map((slotInit: any, index: number) => (
                          <Box
                            key={index}
                            sx={{
                              ...slotChipSx(slot.slot === slotInit?.slot),
                              px: 1,
                            }}
                            onClick={() => handleSelectedTime(slotInit)}
                          >
                            {slotInit?.primeTimeCharge ? (
                              <Typography variant="caption" sx={primeTimeBadgeSx}>
                                {currencyCode}
                                {slotInit?.primeTimeCharge}
                              </Typography>
                            ) : null}
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.85rem" }}
                            >
                              {slotInit?.slot}
                            </Typography>
                          </Box>
                        ))}
                    </Box>
                  )
                )}
              </Box>
            ) : (
              <Box sx={{ py: 2, textAlign: "center", minHeight: 0 }}>
                <Typography variant="subtitle1" fontWeight={800} color="#0b1710">
                  No slots available
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 2, maxWidth: 320, mx: "auto" }}
                >
                  Try another date or call us for help.
                </Typography>
                <Typography
                  component="a"
                  href="tel:+918510000223"
                  sx={{
                    display: "inline-block",
                    border: `1px solid ${alpha("#1f7a4d", 0.35)}`,
                    borderRadius: "999px",
                    px: 2,
                    py: 1,
                    fontWeight: 700,
                    color: SCHEDULE_MINT_DARK,
                    textDecoration: "none",
                  }}
                >
                  +91 85100 00223
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ textAlign: "center", mt: 1.5 }}>
            <Typography variant="caption" color="text.secondary">
              Tip: avoid last‑minute cancellation fees by picking a slot you can
              keep.
            </Typography>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
};

export default ScheduleCartMobile;
